export type Sex = "male" | "female";

/** Mifflin-St Jeor basal metabolic rate (kcal/day). */
export function bmrMifflin(
  sex: Sex,
  weightKg: number,
  heightCm: number,
  age: number,
): number {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return base + (sex === "male" ? 5 : -161);
}

/** Activity multipliers for TDEE. */
export const ACTIVITY = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  veryActive: 1.9,
} as const;
export type ActivityLevel = keyof typeof ACTIVITY;

export function tdee(bmr: number, level: ActivityLevel): number {
  return bmr * ACTIVITY[level];
}

export interface Macros {
  proteinG: number;
  carbsG: number;
  fatG: number;
}

/** Split calories into macros by percentage (protein/carb 4 kcal/g, fat 9). */
export function macrosFromCalories(
  calories: number,
  pctProtein: number,
  pctCarbs: number,
  pctFat: number,
): Macros {
  return {
    proteinG: (calories * (pctProtein / 100)) / 4,
    carbsG: (calories * (pctCarbs / 100)) / 4,
    fatG: (calories * (pctFat / 100)) / 9,
  };
}

/** US Navy body-fat % (circumference method). Hip only used for women. */
export function bodyFatNavy(
  sex: Sex,
  heightCm: number,
  neckCm: number,
  waistCm: number,
  hipCm = 0,
): number | null {
  if (sex === "male") {
    const d = waistCm - neckCm;
    if (d <= 0 || heightCm <= 0) return null;
    return (
      495 /
        (1.0324 - 0.19077 * Math.log10(d) + 0.15456 * Math.log10(heightCm)) -
      450
    );
  }
  const d = waistCm + hipCm - neckCm;
  if (d <= 0 || heightCm <= 0) return null;
  return (
    495 / (1.29579 - 0.35004 * Math.log10(d) + 0.221 * Math.log10(heightCm)) -
    450
  );
}

export type IdealFormula = "devine" | "robinson" | "miller" | "hamwi";

/** Ideal body weight (kg) from height. Classic formulas over 152.4 cm (5 ft). */
export function idealWeight(
  sex: Sex,
  heightCm: number,
  formula: IdealFormula,
): number {
  const inchesOver5ft = Math.max(0, (heightCm - 152.4) / 2.54);
  const male = sex === "male";
  switch (formula) {
    case "devine":
      return (male ? 50 : 45.5) + 2.3 * inchesOver5ft;
    case "robinson":
      return (male ? 52 : 49) + (male ? 1.9 : 1.7) * inchesOver5ft;
    case "miller":
      return (male ? 56.2 : 53.1) + (male ? 1.41 : 1.36) * inchesOver5ft;
    case "hamwi":
      return (male ? 48 : 45.5) + (male ? 2.7 : 2.2) * inchesOver5ft;
  }
}

/** Daily water intake (litres): ~35 ml/kg + 0.35 L per 30 min exercise. */
export function waterIntakeLitres(
  weightKg: number,
  exerciseMin: number,
): number {
  return (weightKg * 35) / 1000 + (Math.max(0, exerciseMin) / 30) * 0.35;
}
